import { motion } from 'framer-motion';
import { MessageCircle, Instagram, Mail, MapPin } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_amazonia-trips-1/artifacts/gy2z1mzk_ayahuascatrip%20logo.png";

const navLinks = [
  { label: 'Experience', href: '#experience' },
  { label: 'Guide', href: '#guide' },
  { label: 'Packages', href: '#packages' },
  { label: 'Safety', href: '#safety' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'FAQ', href: '#faq' },
];

export const FooterSection = ({ whatsappLink }) => {
  const handleNavClick = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-black/50 pt-24 pb-8" data-testid="footer-section">
      {/* Top Border Glow */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-egyptian-gold/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <motion.img
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              src={LOGO_URL}
              alt="Ayahuasca Trip"
              className="h-16 mb-6"
              data-testid="footer-logo"
            />
            <p className="font-body text-gray-400 leading-relaxed mb-6 max-w-md">
              Embark on a transformative journey in the heart of the Amazon. 
              Experience ancient wisdom guided by qualified professionals in a safe, 
              sacred environment where tradition meets modern understanding.
            </p>
            {/* WhatsApp CTA */}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn inline-flex"
              data-testid="footer-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Begin Your Journey
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-white uppercase tracking-wider mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="font-body text-gray-400 hover:text-neon-cyan transition-colors duration-300"
                    data-testid={`footer-nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-white uppercase tracking-wider mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 font-body text-gray-400 hover:text-neon-cyan transition-colors duration-300"
                  data-testid="footer-contact-whatsapp"
                >
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  +55 21 97223-2170
                </a>
              </li>
              <li>
                <a
                  href="mailto:contact@ayahuascatrip.com.br"
                  className="flex items-center gap-3 font-body text-gray-400 hover:text-neon-cyan transition-colors duration-300"
                  data-testid="footer-contact-email"
                >
                  <Mail className="w-5 h-5 text-egyptian-gold" />
                  contact@ayahuascatrip.com.br
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-neon-magenta flex-shrink-0 mt-0.5" />
                  <span className="font-body text-gray-400">
                    Amazon Rainforest, Brazil
                  </span>
                </div>
              </li>
              <li>
                <a
                  href="https://instagram.com/ayahuascatrip"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 font-body text-gray-400 hover:text-neon-cyan transition-colors duration-300"
                  data-testid="footer-contact-instagram"
                >
                  <Instagram className="w-5 h-5 text-pink-500" />
                  @ayahuascatrip
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-8" />

        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-body text-gray-500 text-sm">
            © {currentYear} Ayahuasca Trip. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="#"
              className="font-body text-gray-500 text-sm hover:text-gray-400 transition-colors duration-300"
            >
              Privacy Policy
            </a>
            <a
              href="#"
              className="font-body text-gray-500 text-sm hover:text-gray-400 transition-colors duration-300"
            >
              Terms of Service
            </a>
          </div>
        </div>

        {/* Mystical tagline */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="font-accent text-egyptian-gold/50 text-xs tracking-[0.5em] uppercase">
            Where Ancient Wisdom Meets the Digital Age
          </p>
        </motion.div>
      </div>
    </footer>
  );
};
